<template>
	<div class="flex flex-col h-max px-4 py-7 md:p-dj-40 border border-dj-gray-light/50 rounded-big bg-white">
		<div class="hidden md:flex">
			<BaseCloudImage
				v-if="isShowCompany"
				custom-class="shrink-0 rounded-normal overflow-hidden h-max cursor-pointer"
				:public-id="logo"
				postfix=".jpg"
				:alt="companyName"
				:transformation="[{ width: 80, height: 80, crop: 'scale' }]"
				@click="navigateToCompany"
			/>
			<div class="flex flex-col ml-4">
				<span class="mb-2 text-xl font-medium">
					{{ jobName }}
				</span>
				<span v-if="isShowCompany" class="text-dj-gray-dark text-sm">
					{{ location }}
				</span>
			</div>
		</div>

		<div v-if="filteredJobTags.length > 0" class="hidden md:flex flex-wrap mt-5 text-dj-gray-dark">
			<span class="text-xs" v-for="(tag, index) in filteredJobTags" :key="tag.id || index">
				{{ tag.name ? tag.name[locale] : tag[locale] }}
				<span class="mx-2" v-if="index != filteredJobTags.length - 1">|</span>
			</span>
		</div>

		<div class="hidden md:block h-[1px] w-full mt-5 bg-dj-gray-light"></div>

		<div class="flex items-start my-5 text-sm">
			<span class="text-dj-gray-dark">{{ $t("job.card.deadline") }}</span>
			<div class="flex flex-col items-end ml-auto pl-3">
				<span
					v-if="expiresDate === 'unlimited'"
					class="inline-flex items-center justify-end gap-1.5 font-medium max-w-[12rem] text-right"
				>
					<span>{{ $t("job.card.deadlineUnlimited") }}</span>
					<span
						class="shrink-0 cursor-help text-dj-gray"
						:title="$t('job.card.deadlineUnlimitedTooltip')"
						:aria-label="$t('job.card.deadlineUnlimitedTooltip')"
					>
						<BaseIcon icon="material-symbols:info-outline" class="text-base" color="currentColor" />
					</span>
				</span>
				<span class="font-medium" v-else-if="expiresDate != 'expired'">{{ formattedExpireDate }}</span>
				<span v-if="!expiredJob && showRemainingDays" class="text-xs text-dj-red">
					{{ $t("job.card.remainingDays", { days: remainingDays }) }}
				</span>
				<span v-if="expiredJob" class="text-xs text-dj-red">{{ $t("job.card.expired") }}</span>
			</div>
		</div>

		<div class="flex flex-col">
			<template v-if="alreadyApplied">
				<NuxtLink :to="`/${locale}/my-applications`" class="mx-auto text-sm text-dj-red">
					{{ $t("job.card.alreadyApplied") }}
				</NuxtLink>
			</template>
			<template v-else>
				<BaseButton @click="$emit('apply')" :disabled="expiredJob" type="primary-red">
					{{ $t("job.card.jobApplication") }}
				</BaseButton>
			</template>
			<div
				@click="saveJob"
				:class="[
					'flex items-center justify-center mt-5 cursor-pointer',
					expiredJob ? 'text-dj-gray' : 'text-dj-blue',
				]"
			>
				<BaseIcon
					class="mr-[10px] text-xl"
					:icon="savedJob ? 'ic:outline-favorite' : 'ic:outline-favorite-border'"
					color="currentColor"
				/>
				<span class="text-sm">{{ savedJob ? $t("job.card.deleteJob") : $t("job.card.saveJob") }}</span>
			</div>
		</div>

		<div v-if="isShowCompany" class="h-[1px] w-full my-5 bg-dj-gray-light"></div>

		<div class="flex flex-col text-center text-dj-gray-dark" v-if="isShowCompany">
			<span class="font-medium">{{ $t("company.card.getInformed") }}</span>
			<span class="text-sm mt-2 mb-5">{{ $t("company.card.subscribe") }}</span>
			<BaseButton @click="toggleSavedCompany" type="secondary-red">
				<BaseIcon
					class="text-[24px]"
					:icon="isSavedCompany() ? 'ic:outline-favorite' : 'ic:outline-favorite-border'"
					color="#E1415A"
				/>
				<span class="ml-3">
					{{ isSavedCompany() ? $t("company.card.alreadyFollow") : $t("company.card.followCompany") }}
				</span>
			</BaseButton>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { type JobItem, getJobSlug } from "@@/composables/useJob";
import { Tags } from "~~/types/tags";

export default defineComponent({
	name: "JobProfileCard",
	props: {
		jobData: {
			type: Object as PropType<JobItem>,
			default: null,
		},
		jobName: {
			type: String,
			default: "Job",
		},
		location: {
			type: String,
			default: "company address",
		},
		logo: {
			type: String,
		},
		jobTags: {
			type: Object as PropType<Tags>,
		},
		expiresDate: {
			type: String,
			required: true,
		},
		companyName: {
			type: String,
			required: true,
		},
		alreadyApplied: {
			type: Boolean,
			required: true,
		},
		savedJob: {
			type: Boolean,
			default: false,
		},
		companyId: {
			type: Number,
			required: true,
		},
	},
	emits: ["apply", "onSavedChange"],
	setup(props, { emit }) {
		const isAuth = useState("isAuth");
		const { t, locale } = useI18n();
		const app = useNuxtApp();
		const router = useRouter();
		const route = useRoute();
		const gtm = useGTM();

		const companySlug = ref(props.jobData?.company?.slug);
		const isShowCompany = ref<boolean>(true);
		isShowCompany.value = props.jobData.company_id !== null;

		const filteredJobTags = computed(() => {
			if (!props.jobTags) return [];

			const jobLevel = props.jobTags.job_level ? [props.jobTags.job_level] : [];

			const jobTypes = props.jobTags.job_type?.data || [];
			const officeLocations = props.jobTags.office_location?.data || [];

			return [...jobLevel, ...jobTypes, ...officeLocations];
		});

		function saveJob() {
			if (!expiredJob.value) {
				if (isAuth.value) {
					const isCurrentlySaved = props.savedJob;

					// 1. Optimistic Update: Change the UI instantly
					emit("onSavedChange", !isCurrentlySaved);

					if (isCurrentlySaved) {
						// Remove job
						$fetch("/api/save-job", {
							method: "POST",
							body: {
								token: useCookie("token").value,
								xsrf: useCookie("XSRF-TOKEN").value,
								locale: locale.value,
								jobSlug: getJobSlug(props.jobData, locale.value),
								companySlug: companySlug.value,
								operation: "remove-saved-job-from-user",
							},
						})
							.then(() => {
								app.$openToast({
									message: t("job.alert.successfulRemove"),
									description: t("job.alert.successfulRemoveDesc"),
									type: "success",
								});
								useAuth.updateUser();
							})
							.catch(() => {
								// Revert the UI if the API fails
								emit("onSavedChange", isCurrentlySaved);
							});
					} else {
						// Save job
						$fetch("/api/save-job", {
							method: "POST",
							body: {
								token: useCookie("token").value,
								xsrf: useCookie("XSRF-TOKEN").value,
								locale: locale.value,
								jobSlug: getJobSlug(props.jobData, locale.value),
								companySlug: companySlug.value,
								operation: "save-job-to-user",
							},
						})
							.then(() => {
								app.$openToast({
									message: t("job.alert.successfulSave"),
									description: t("job.alert.successfulSaveDesc"),
									type: "success",
								});
								useAuth.updateUser();
							})
							.catch(() => {
								// Revert the UI if the API fails
								emit("onSavedChange", isCurrentlySaved);
							});
					}
				} else {
					router.push({
						path: `/${locale.value}/login`,
						query: { r: route.fullPath },
					});
				}
			}
		}

		const formattedExpireDate = computed(() => {
			if (props.expiresDate === "unlimited") {
				return "";
			}
			const date = new Date(props.expiresDate.replace(" ", "T"));
			const options: Intl.DateTimeFormatOptions = { month: "long", day: "2-digit" };
			if (date.getFullYear() !== new Date().getFullYear()) {
				options.year = "numeric";
			}
			return new Intl.DateTimeFormat(locale.value, options).format(date);
		});

		const showRemainingDays = ref(false);
		const remainingDays = props.jobData.remainingDays;
		const expiredJob = props.jobData.expiredJob;

		onMounted(() => {
			const days = remainingDays;
			if (days > 0 && days <= 3) {
				showRemainingDays.value = true;
			}
		});

		function toggleSavedCompany() {
			if (isAuth.value) {
				$fetch(`/api/save-company`, {
					method: "POST",
					body: {
						companySlug: String(companySlug.value),
						xsrf: useAuth.getXsrfToken(),
						token: useCookie("token").value,
						locale: locale.value,
						operation: isSavedCompany() == true ? "remove-saved-company-from-user" : "save-company-to-user", // Save or Unsave
					},
				})
					.then(() => {
						// GTM tracking
						gtm.trackEvent({
							event: isSavedCompany() == true ? "b2c-company-unfollow" : "b2c-company-follow",
						});

						useAuth.updateUser();
					})
					.catch((error) => {
						console.error("Card toggleSavedCompany", error);
					});
			} else {
				router.push(`/${locale.value}/login`);
			}
		}

		function isSavedCompany() {
			return isAuth.value && useAuth.isLovedCompany(props.companyId);
		}

		function navigateToCompany() {
			router.push({
				path: `/${locale.value}/company/${companySlug.value}`,
			});
		}

		return {
			locale,
			saveJob,
			formattedExpireDate,
			remainingDays,
			showRemainingDays,
			expiredJob,
			navigateToCompany,
			toggleSavedCompany,
			isSavedCompany,
			isShowCompany,
			filteredJobTags,
		};
	},
});
</script>
